import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Context
import { AuthProvider } from "./context/AuthContext";
import { AdminCountsProvider } from "./context/AdminCountsContext";

// Public Pages
import HomePage from "./pages/HomePage";
import CityPage from "./pages/CityPage";
import ListingPage from "./pages/ListingPage";
import HostBenefitsPage from "./pages/HostBenefitsPage";
import SearchResultsPage from "./pages/SearchResultsPage";

// Legal Pages
import TermsPage from "./pages/legal/TermsPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import PaymentsPage from "./pages/legal/PaymentsPage";
import CancellationPage from "./pages/legal/CancellationPage";
import HostObligationsPage from "./pages/legal/HostObligationsPage";
import GuestResponsibilitiesPage from "./pages/legal/GuestResponsibilitiesPage";
import PlatformDisclaimerPage from "./pages/legal/PlatformDisclaimerPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

// App Pages (Protected)
import AppLayout from "./layouts/AppLayout";
import GuestDashboard from "./pages/app/GuestDashboard";
import GuestBookings from "./pages/app/GuestBookings";
import GuestReviews from "./pages/app/GuestReviews";
import BookingDetail from "./pages/app/BookingDetail";
import HostDashboard from "./pages/app/HostDashboard";
import HostProperties from "./pages/app/HostProperties";
import HostPropertyForm from "./pages/app/HostPropertyForm";
import HostReservations from "./pages/app/HostReservations";
import HostPayouts from "./pages/app/HostPayouts";
import HostOnboarding from "./pages/app/HostOnboarding";
import HostReviews from "./pages/app/HostReviews";
import HostCalendar from "./pages/app/HostCalendar";
import ProfileSettings from "./pages/app/ProfileSettings";
import SavedProperties from "./pages/app/SavedProperties";
import StripeReturnPage from "./pages/app/StripeReturnPage";
import AdminDashboard from "./pages/app/AdminDashboard";
import AdminUsers from "./pages/app/AdminUsers";
import AdminProperties from "./pages/app/AdminProperties";
import AdminBookings from "./pages/app/AdminBookings";
import AdminHosts from "./pages/app/AdminHosts";
import AdminAuditLog from "./pages/app/AdminAuditLog";

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
    <AdminCountsProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/search" element={<SearchResultsPage />} />
          <Route path="/:citySlug" element={<CityPage />} />
          <Route path="/listing/:slug" element={<ListingPage />} />
          <Route path="/host" element={<HostBenefitsPage />} />
          
          {/* Legal Routes */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/cancellation" element={<CancellationPage />} />
          <Route path="/host-obligations" element={<HostObligationsPage />} />
          <Route path="/guest-responsibilities" element={<GuestResponsibilitiesPage />} />
          <Route path="/platform-disclaimer" element={<PlatformDisclaimerPage />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Stripe Return - handles its own auth check */}
          <Route path="/app/stripe/return" element={<StripeReturnPage />} />
          
          {/* App Routes (Protected) */}
          <Route path="/app" element={<AppLayout />}>
            {/* Guest Routes */}
            <Route index element={
              <ProtectedRoute>
                <GuestDashboard />
              </ProtectedRoute>
            } />
            <Route path="bookings" element={
              <ProtectedRoute>
                <GuestBookings />
              </ProtectedRoute>
            } />
            <Route path="bookings/:bookingId" element={
              <ProtectedRoute>
                <BookingDetail />
              </ProtectedRoute>
            } />
            <Route path="reviews" element={
              <ProtectedRoute>
                <GuestReviews />
              </ProtectedRoute>
            } />
            <Route path="saved" element={
              <ProtectedRoute>
                <SavedProperties />
              </ProtectedRoute>
            } />
            
            {/* Host Routes */}
            <Route path="host" element={
              <ProtectedRoute requiredRole="host">
                <HostDashboard />
              </ProtectedRoute>
            } />
            <Route path="host/onboarding" element={
              <ProtectedRoute requiredRole="host">
                <HostOnboarding />
              </ProtectedRoute>
            } />
            <Route path="host/properties" element={
              <ProtectedRoute requiredRole="host">
                <HostProperties />
              </ProtectedRoute>
            } />
            <Route path="host/properties/new" element={
              <ProtectedRoute requiredRole="host">
                <HostPropertyForm />
              </ProtectedRoute>
            } />
            <Route path="host/properties/:propertyId" element={
              <ProtectedRoute requiredRole="host">
                <HostPropertyForm />
              </ProtectedRoute>
            } />
            <Route path="host/reservations" element={
              <ProtectedRoute requiredRole="host">
                <HostReservations />
              </ProtectedRoute>
            } />
            <Route path="host/payouts" element={
              <ProtectedRoute requiredRole="host">
                <HostPayouts />
              </ProtectedRoute>
            } />
            <Route path="host/reviews" element={
              <ProtectedRoute requiredRole="host">
                <HostReviews />
              </ProtectedRoute>
            } />
            <Route path="host/calendar" element={
              <ProtectedRoute requiredRole="host">
                <HostCalendar />
              </ProtectedRoute>
            } />
            
            {/* Profile Route (all authenticated users) */}
            <Route path="profile" element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="admin" element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="admin/users" element={
              <ProtectedRoute requiredRole="admin">
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="admin/hosts" element={
              <ProtectedRoute requiredRole="admin">
                <AdminHosts />
              </ProtectedRoute>
            } />
            <Route path="admin/properties" element={
              <ProtectedRoute requiredRole="admin">
                <AdminProperties />
              </ProtectedRoute>
            } />
            <Route path="admin/bookings" element={
              <ProtectedRoute requiredRole="admin">
                <AdminBookings />
              </ProtectedRoute>
            } />
            <Route path="admin/audit-log" element={
              <ProtectedRoute requiredRole="admin">
                <AdminAuditLog />
              </ProtectedRoute>
            } />
          </Route>
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AdminCountsProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
